import { Formik } from 'formik';
import React, { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, InlineNotification, Select, SelectItem, Stack } from '@carbon/react';
import { useBilling } from 'src/hooks/useBilling';
import { OrgMemberRow } from 'src/hooks/useOrgMembers';

interface MemberRoleFormProps {
  onSubmit: (values: any) => void;
  errorMessage: string | null;
  initialValues: OrgMemberRow | null | undefined;
}

/**
 * The only editable field on a member is the role — name and email belong to the person's
 * account, not to their membership, so they are read-only context here rather than inputs.
 *
 * This replaces the inline per-row <Select> the hand-written table used. That control wrote
 * on every change with no confirmation step and no error surface; going through the CRUD
 * modal gives both, and keeps the row's mutation out of the table body.
 */
const MemberRoleForm = (props: MemberRoleFormProps, ref) => {
  const { t } = useTranslation();
  const { data: billing } = useBilling();
  const adminLocked = Boolean(billing?.enabled && !billing.limits.adminRole);
  const formikRef = useRef<any>();

  useImperativeHandle(ref, () => ({
    submitForm: formikRef.current?.submitForm,
    setFieldValue: formikRef.current?.setFieldValue,
    resetForm: () => formikRef.current?.resetForm({ values: { id: '', role: 'member' } }),
  }));

  return (
    <div>
      {props.errorMessage && <InlineNotification subtitle={props.errorMessage} />}
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          id: props.initialValues?.id ?? '',
          role: props.initialValues?.role ?? 'member',
        }}
        onSubmit={props.onSubmit}
      >
        {({ values, setFieldValue }) => (
          <FormGroup legendText={''}>
            <Stack gap={5}>
              {props.initialValues && (
                <p>
                  {props.initialValues.user?.name} ({props.initialValues.user?.email})
                </p>
              )}
              <Select
                id="member-role"
                labelText={t('common.role')}
                value={values.role}
                onChange={(e) => setFieldValue('role', e.target.value)}
              >
                <SelectItem value="owner" text={t('orgSettings.members.owner')} />
                <SelectItem
                  value="admin"
                  disabled={adminLocked}
                  text={
                    adminLocked
                      ? `${t('orgSettings.members.admin')} · ${t('billing.locked')}`
                      : t('orgSettings.members.admin')
                  }
                />
                <SelectItem value="member" text={t('orgSettings.members.member')} />
              </Select>
            </Stack>
          </FormGroup>
        )}
      </Formik>
    </div>
  );
};

export default React.forwardRef(MemberRoleForm);
